import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Mail, MessageSquare } from "lucide-react";

import SidebarLayout from "@/components/SidebarLayout";
import { api } from "@/lib/api";


const STATUS_OPTIONS = ["new", "contacted", "closed_won", "closed_lost"];


export default function AdminEnterpriseLeads() {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // lead being edited

    const load = async (status) => {
        setLoading(true);
        try {
            const d = await api.adminEnterpriseLeads(status || undefined);
            setData(d);
        } catch (e) {
            toast.error(e.message || "Failed to load leads");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(filter);
    }, [filter]);

    const patch = async (leadId, body) => {
        try {
            await api.adminPatchEnterpriseLead(leadId, body);
            toast.success("Updated");
            load(filter);
            setEditing(null);
        } catch (e) {
            toast.error(e.message || "Update failed");
        }
    };

    return (
        <SidebarLayout>
            <div className="max-w-6xl mx-auto py-8 px-4">
                <h1 className="text-2xl font-semibold">Enterprise leads</h1>
                <p className="text-sm text-text-secondary">
                    Richieste "Contact sales" dalla pricing page.
                </p>

                {/* Filter tabs */}
                <div className="mt-6 flex flex-wrap gap-2" data-testid="ent-status-tabs">
                    <FilterTab
                        active={filter === ""}
                        onClick={() => setFilter("")}
                        label="All"
                        count={
                            data
                                ? Object.values(data.counts).reduce((a, b) => a + b, 0)
                                : 0
                        }
                        testid="ent-tab-all"
                    />
                    {STATUS_OPTIONS.map((s) => (
                        <FilterTab
                            key={s}
                            active={filter === s}
                            onClick={() => setFilter(s)}
                            label={s.replace(/_/g, " ")}
                            count={data?.counts?.[s] || 0}
                            testid={`ent-tab-${s}`}
                        />
                    ))}
                </div>

                {loading || !data ? (
                    <p className="mt-6 text-sm text-text-secondary">Loading…</p>
                ) : (
                    <div className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
                        {data.items.length === 0 ? (
                            <p className="p-8 text-sm text-text-secondary text-center">
                                Nessun lead in questa categoria.
                            </p>
                        ) : (
                            <table
                                className="min-w-full text-sm"
                                data-testid="ent-leads-table"
                            >
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-widest text-text-secondary border-b border-[var(--color-border)]">
                                        <th className="px-4 py-3">Company</th>
                                        <th className="px-4 py-3">Contact</th>
                                        <th className="px-4 py-3">Volume</th>
                                        <th className="px-4 py-3">Use case</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((lead) => (
                                        <tr
                                            key={lead.id}
                                            data-testid={`ent-lead-row-${lead.id}`}
                                            className="border-b border-[var(--color-border)] last:border-0 align-top"
                                        >
                                            <td className="px-4 py-3">
                                                <p className="font-medium">{lead.company}</p>
                                                <p className="text-[11px] text-text-secondary">
                                                    {new Date(lead.created_at).toLocaleString()}
                                                </p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <p>{lead.name}</p>
                                                <a
                                                    href={`mailto:${lead.email}`}
                                                    className="text-[11px] text-[var(--color-secondary)] inline-flex items-center gap-1 hover:underline"
                                                >
                                                    <Mail className="h-3 w-3" /> {lead.email}
                                                </a>
                                                {lead.phone && (
                                                    <p className="text-[11px] text-text-secondary">
                                                        {lead.phone}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-text-secondary text-xs">
                                                {lead.expected_volume || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-text-secondary text-xs max-w-xs">
                                                <span className="line-clamp-3">
                                                    {lead.use_case || "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                    data-testid={`ent-lead-status-${lead.id}`}
                                                    value={lead.status}
                                                    onChange={(e) =>
                                                        patch(lead.id, {
                                                            status: e.target.value,
                                                        })
                                                    }
                                                    className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs"
                                                >
                                                    {STATUS_OPTIONS.map((s) => (
                                                        <option key={s} value={s}>
                                                            {s.replace(/_/g, " ")}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    onClick={() =>
                                                        setEditing(
                                                            editing === lead.id ? null : lead.id,
                                                        )
                                                    }
                                                    className="text-[11px] text-text-secondary hover:text-text-main inline-flex items-center gap-1"
                                                >
                                                    <MessageSquare className="h-3 w-3" /> Note
                                                </button>
                                                {editing === lead.id && (
                                                    <NoteEditor
                                                        initial={lead.admin_note || ""}
                                                        onSave={(note) =>
                                                            patch(lead.id, { admin_note: note })
                                                        }
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
}

function FilterTab({ active, onClick, label, count, testid }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest transition ${
                active
                    ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                    : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
            }`}
        >
            {label} <span className="tabular-nums">{count}</span>
        </button>
    );
}

function NoteEditor({ initial, onSave }) {
    const [val, setVal] = useState(initial);
    return (
        <div className="mt-2 flex flex-col gap-2 w-64">
            <textarea
                data-testid="ent-lead-note-input"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                rows={3}
                className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs"
            />
            <button
                data-testid="ent-lead-note-save"
                onClick={() => onSave(val)}
                className="inline-flex items-center gap-1 self-end rounded-full bg-[var(--color-primary)]/20 px-3 py-1 text-[10px] text-[var(--color-primary)]"
            >
                <Check className="h-3 w-3" /> Save
            </button>
        </div>
    );
}
